import { Capacitor } from '@capacitor/core';
import { BASE } from './client';

/**
 * Web-play side of the acquisition funnel (see the LP's mirror in
 * goodtime-lp/src/lib/funnel.ts + the server's AnalyticsFunnelService).
 *
 * <p>The crucial job here is the **player↔attribution link**: web-play reads the
 * {@code gt_attr} token the LP minted on a {@code .goodtime.casino} cookie
 * (readable from the play subdomain), and once a player id exists it fires a
 * beacon carrying BOTH. That link is what lets the Stripe purchase webhook —
 * which only knows the player id — attribute the conversion back to the ad
 * click. Without it a web purchase still counts by-platform, just unattributed.
 *
 * <p>Platform comes from Capacitor: {@code web} in the browser, {@code ios} /
 * {@code android} in the native shell (so native funnel-top events flow through
 * here too; their upstream ad attribution is the known deferred-deep-link gap).
 * On native there's no LP cookie, so a fresh organic token is minted.
 */

const COOKIE_NAME = 'gt_attr';
const STORE_KEY = 'goodtime.attribution';

interface Attribution {
  id: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  lp?: string; // landing-page variant carried from the LP (part of the funnel-combination id)
  country?: string; // ISO-3166 alpha-2, resolved from the IP (GeoJS / api.country.is); shared via the LP cookie when present
}

let cached: Attribution | null = null;
let countryResolving = false;

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'a-' + Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m && m[1] != null ? decodeURIComponent(m[1]) : undefined;
}

function platform(): string {
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android' ? p : 'web';
}

/** Resolve the attribution token: the LP's shared cookie wins (preserves the
 *  ad-click campaign), else a stored token, else a freshly minted organic one. */
function getAttribution(): Attribution {
  if (cached) return cached;

  const fromCookie = readCookie(COOKIE_NAME);
  if (fromCookie) {
    try {
      cached = JSON.parse(fromCookie) as Attribution;
      return cached;
    } catch {
      /* malformed — re-mint */
    }
  }
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      cached = JSON.parse(raw) as Attribution;
      return cached;
    }
  } catch {
    /* private mode */
  }

  const qs = new URLSearchParams(window.location.search);
  const pick = (k: string) => {
    const v = qs.get(k);
    return v && v.trim() ? v.trim() : undefined;
  };
  cached = {
    id: uuid(),
    utmSource: pick('utm_source'),
    utmMedium: pick('utm_medium'),
    utmCampaign: pick('utm_campaign'),
    utmContent: pick('utm_content'),
    utmTerm: pick('utm_term'),
    fbclid: pick('fbclid'),
    lp: pick('lp'),
  };
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(cached));
  } catch {
    /* ignore */
  }
  return cached;
}

/** Caller-IP → ISO country: CORS-enabled, key-free. GeoJS first, api.country.is
 *  as a fallback — both resolve the requester's own IP and return a {country}
 *  field. (Moved off Book Magic's checkip→ipapi.co chain: ipapi.co now sits
 *  behind a Cloudflare bot-challenge and 403s.) */
const GEO_ENDPOINTS = [
  'https://get.geojs.io/v1/ip/country.json',
  'https://api.country.is/',
];

async function lookupCountry(): Promise<string | null> {
  for (const url of GEO_ENDPOINTS) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const data = (await res.json()) as { country?: string };
      const cc = String(data?.country ?? '').trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(cc)) return cc;
    } catch {
      /* try the next endpoint */
    }
  }
  return null;
}

/** Resolve the visitor's country when the LP cookie didn't already carry one (a
 *  web-play-direct or native visitor). Persists it onto the token so later
 *  beacons include it; keeps ONLY the 2-letter code, never the IP. Best-effort,
 *  once per token. */
async function resolveCountry(): Promise<void> {
  const a = getAttribution();
  if (a.country || countryResolving || typeof fetch === 'undefined') return;
  countryResolving = true;
  try {
    const cc = await lookupCountry();
    if (!cc) return;
    a.country = cc;
    cached = a;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(a));
    } catch {
      /* ignore */
    }
  } finally {
    countryResolving = false;
  }
}

/** Fire a funnel beacon. Best-effort, non-blocking, keepalive so it survives the
 *  navigation a subscribe redirect triggers. */
export function funnelBeacon(
  step: string,
  opts?: { playerId?: string | null; detail?: string; durationMs?: number },
): void {
  try {
    const a = getAttribution();
    void resolveCountry(); // kick off (or no-op if already resolved); fills later beacons
    // Meta match cookies (web only — native has no _fbp/_fbc; that's the known
    // deferred-attribution gap). Read at send time; derive fbc from fbclid if
    // the _fbc cookie isn't set yet.
    const fbp = readCookie('_fbp');
    const fbc = readCookie('_fbc') ?? (a.fbclid ? `fb.1.${Date.now()}.${a.fbclid}` : undefined);
    const body = JSON.stringify({
      attributionId: a.id,
      playerId: opts?.playerId ?? undefined,
      step,
      platform: platform(),
      stepDetail: opts?.detail,
      utmSource: a.utmSource,
      utmMedium: a.utmMedium,
      utmCampaign: a.utmCampaign,
      utmContent: a.utmContent,
      utmTerm: a.utmTerm,
      fbclid: a.fbclid,
      fbp,
      fbc,
      referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
      eventId: uuid(),
      durationMs: opts?.durationMs != null ? String(Math.round(opts.durationMs)) : undefined,
      lp: a.lp,
      country: a.country,
    });
    void fetch(`${BASE}/analytics/funnel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {
      /* analytics never surfaces an error to the player */
    });
  } catch {
    /* ignore */
  }
}

/** Read the persisted player id the app stores at boot (see initSessionTime's
 *  send()). Best-effort: private mode / no-storage returns null, never throws. */
function storedPlayerId(): string | null {
  try {
    return localStorage.getItem('goodtime.playerId');
  } catch {
    return null;
  }
}

const MILESTONES_KEY = 'goodtime.funnel.milestones';

/** Read the set of milestone steps already fired on this device. */
function firedMilestones(): string[] {
  try {
    const raw = localStorage.getItem(MILESTONES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed.filter((s) => typeof s === 'string') as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Fire a one-time-per-device milestone beacon for {@code step}. The funnel
 * milestones (first_spin, room_joined, invite_sent, returning) measure whether
 * a player actually progresses — not just "opened". Guarded by a localStorage
 * set so each step beacons AT MOST ONCE per device, ever.
 *
 * <p>Best-effort and non-blocking: storage failures (private mode) are swallowed
 * and the call never throws into gameplay. {@code playerId} falls back to the
 * persisted {@code goodtime.playerId} when omitted.
 */
export function funnelMilestone(step: string, playerId?: string | null): void {
  try {
    const fired = firedMilestones();
    if (fired.includes(step)) return;
    fired.push(step);
    try {
      localStorage.setItem(MILESTONES_KEY, JSON.stringify(fired));
    } catch {
      /* private mode — still beacon below; we just can't dedupe across loads */
    }
    funnelBeacon(step, { playerId: playerId ?? storedPlayerId() });
  } catch {
    /* analytics never surfaces an error to the player */
  }
}

const FIRST_DAY_KEY = 'goodtime.funnel.firstDay';

/** Today's local calendar date as a plain {@code YYYY-MM-DD} string. */
function localDay(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Detect a return visit on a LATER calendar day and fire the {@code returning}
 * milestone once. On the first ever call we record today's local date and stay
 * silent (this is day one). On a later day, once, we fire the milestone (whose
 * own guard makes it once-ever). Best-effort; never throws.
 */
export function trackReturning(playerId?: string | null): void {
  try {
    let firstDay: string | null = null;
    try {
      firstDay = localStorage.getItem(FIRST_DAY_KEY);
    } catch {
      /* private mode — can't track return visits */
      return;
    }
    const today = localDay();
    if (!firstDay) {
      try {
        localStorage.setItem(FIRST_DAY_KEY, today);
      } catch {
        /* ignore */
      }
      return; // first day — not a return visit
    }
    if (today > firstDay) {
      funnelMilestone('returning', playerId ?? storedPlayerId());
    }
  } catch {
    /* analytics never surfaces an error to the player */
  }
}

let sessionTimeInit = false;

/**
 * Track active (tab-visible) on-page time and beacon it once as a `session` step
 * on hide/pagehide — the engagement signal for the analytics portal. Pauses the
 * clock while hidden so background time doesn't count; reads the playerId at send
 * time so the session ties to the player when known. Idempotent (StrictMode-safe).
 * Sent at most once per load; a mobile tab-switch may bank a partial duration (a
 * v1 trade-off — better a partial session than a lost one).
 */
export function initSessionTime(): void {
  if (sessionTimeInit || typeof document === 'undefined') return;
  sessionTimeInit = true;

  let activeMs = 0;
  let resumedAt = Date.now();
  let sent = false;

  const bank = () => {
    if (document.visibilityState === 'visible') activeMs += Date.now() - resumedAt;
    resumedAt = Date.now();
  };
  const send = () => {
    if (sent) return;
    bank();
    if (activeMs <= 0) return;
    sent = true;
    let playerId: string | null = null;
    try {
      playerId = localStorage.getItem('goodtime.playerId');
    } catch {
      /* private mode */
    }
    funnelBeacon('session', { playerId, durationMs: activeMs });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send();
    else resumedAt = Date.now();
  });
  window.addEventListener('pagehide', send);
}
